import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  viewChild,
} from '@angular/core';
import { TransactionDataSource } from '../transaction-data-source.class';
import {
  EDIT_MANY_TRANSACTIONS_MODAL_CONTENT_CLASS,
  EditTransactionsDialogComponent,
} from '../edit-transactions-dialog/edit-transactions-dialog.component';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import {
  AcceptPendingAction,
  Bucket,
  Transaction,
  EditManyTransactionsAction,
  EditManyTransactionsActionValue,
  TransactionDateHeader,
  TransactionListRow,
} from 'src/app/types';
import { BucketService } from 'src/app/services/bucket.service';
import { UiDialogService } from '../../ui/dialog/dialog.service';
import { toast } from '../../ui/toast/sonner';
import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { NgIcon } from '@ng-icons/core';
import { HlmIcon } from '../../ui/helm/icon/src/lib/hlm-icon';
import { TransactionListItemComponent } from '../transaction-list-item/transaction-list-item.component';
import { BucketSelectorComponent } from '../../shared/bucket-selector/bucket-selector.component';

@Component({
  selector: 'app-infinite-transaction-list',
  templateUrl: './infinite-transaction-list.component.html',
  styleUrl: './infinite-transaction-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    UiButtonDirective,
    NgIcon,
    HlmIcon,
    TransactionListItemComponent,
    BucketSelectorComponent,
    AsyncPipe,
    CurrencyPipe,
    DatePipe,
  ],
})
export class InfiniteTransactionListComponent implements AfterViewInit, OnChanges, OnDestroy {
  private dialog = inject(UiDialogService);
  private bucketService = inject(BucketService);

  readonly viewPort = viewChild(CdkVirtualScrollViewport);

  readonly transactionDataSource = input.required<TransactionDataSource>();
  readonly options = input<{ isInDrawer?: boolean; bucketId?: string }>({});
  readonly scrollResetKey = input<unknown>();

  readonly bucketsQuery = injectQuery(() => this.bucketService.currentMonthBucketsQueryOptions());
  readonly bucketIdentitiesQuery = injectQuery(() => this.bucketService.allBucketsQueryOptions());
  readonly buckets = computed(() =>
    (this.bucketsQuery.data() ?? []).filter(bucket => {
      if (this.options().bucketId) return bucket.id !== this.options().bucketId;
      return true;
    }),
  );
  readonly bucketIdentities = computed(() => this.bucketIdentitiesQuery.data()?.data ?? this.buckets());

  hasSelectedTransactions$ = (dataSource: TransactionDataSource): Observable<boolean> =>
    dataSource.selectedTransactions$.pipe(
      map(selected => selected.length > 0),
      tap(hasSelected => {
        if (!hasSelected) this._selectedBucket.next(null);
      }),
    );

  selectedTransactions$ = (dataSource: TransactionDataSource): Observable<Transaction[]> =>
    dataSource.selectedTransactions$;

  _selectedBucket = new BehaviorSubject<Bucket | null>(null);
  selectedBucket$ = this._selectedBucket.asObservable();
  private resizeObserver?: ResizeObserver;
  private readonly pendingAcceptances = toSignal(
    toObservable(this.transactionDataSource).pipe(switchMap(dataSource => dataSource.pendingAcceptances$())),
    { initialValue: [] as Array<{ transactionId: string; date: string; bucketId: string }> },
  );
  private readonly isUnbucketedOnly = toSignal(
    toObservable(this.transactionDataSource).pipe(switchMap(dataSource => dataSource.isUnbucketedOnly$)),
    { initialValue: false },
  );
  readonly showInlineBucketSelector = computed(() => this.isUnbucketedOnly() && !this.options().isInDrawer);
  readonly showPendingAcceptanceActions = computed(() => this.isUnbucketedOnly() && !this.options().isInDrawer);
  readonly pendingAcceptanceCountByDate = computed(() => {
    const countByDate = new Map<string, number>();
    this.pendingAcceptances().forEach(pending => {
      countByDate.set(pending.date, (countByDate.get(pending.date) ?? 0) + 1);
    });
    return countByDate;
  });

  pendingCountForDate(date: string): number {
    if (!this.showPendingAcceptanceActions()) return 0;
    return this.pendingAcceptanceCountByDate().get(date) ?? 0;
  }

  acceptDay(date: string) {
    const transactionDataSource = this.transactionDataSource();
    combineLatest([transactionDataSource.pendingForDate$(date), transactionDataSource.transactions$])
      .pipe(take(1))
      .subscribe(([pending, transactions]) => {
        if (!pending.length) return;
        const txById = new Map(transactions.map(t => [t.id, t]));
        const snapshot: Array<{ transactionId: string; bucketId: string | null }> = pending.map(p => ({
          transactionId: p.transactionId,
          bucketId: txById.get(p.transactionId)?.bucketId ?? null,
        }));

        const action: AcceptPendingAction = { type: 'ACCEPT_PENDING', value: pending };
        transactionDataSource.dispatch(action);

        const count = pending.length;
        toast.success(`Saved ${count} ${count === 1 ? 'transaction' : 'transactions'}`, {
          duration: 6000,
          action: {
            label: 'Undo',
            onClick: () => {
              const undoAction: AcceptPendingAction = { type: 'ACCEPT_PENDING', value: snapshot };
              transactionDataSource.dispatch(undoAction);
            },
          },
        });
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const viewPort = this.viewPort();
    if (!viewPort) return;

    if (changes['scrollResetKey'] && !changes['scrollResetKey'].firstChange) viewPort.scrollToIndex(0);
    this.scheduleViewportSync();
  }

  ngAfterViewInit() {
    const viewPort = this.viewPort();
    if (!viewPort) return;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleViewportSync());
      this.resizeObserver.observe(viewPort.elementRef.nativeElement);
    }

    this.scheduleViewportSync();
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  trackByListRow(_index: number, row: TransactionListRow) {
    if (row._objectType === 'dateHeader') return `date-${row.date}`;
    return (row as Transaction).id;
  }

  isDateHeader(row: TransactionListRow): row is TransactionDateHeader {
    return row._objectType === 'dateHeader';
  }

  isTransaction(row: TransactionListRow): row is Transaction {
    return row._objectType !== 'dateHeader';
  }

  openEditDialog() {
    const transactionDataSource = this.transactionDataSource();
    transactionDataSource.selectedTransactions$.pipe(take(1)).subscribe(selectedTransactions => {
      this.dialog.openModal(EditTransactionsDialogComponent, {
        ariaLabel: 'Edit transactions',
        contentClass: selectedTransactions.length > 1 ? EDIT_MANY_TRANSACTIONS_MODAL_CONTENT_CLASS : undefined,
        data: {
          transactionDataSource,
        },
      });
    });
  }

  onSuggestedBucketChange(suggestedBuckets: Bucket[]) {
    const bucket = suggestedBuckets[0];
    if (bucket) this._selectedBucket.next(bucket);
  }

  changeSelectedBucket(bucket: Bucket) {
    this._selectedBucket.next(bucket);
  }

  onSaveBucket(bucket: Bucket, selectedTransactions: Transaction[]) {
    const transactionIds = selectedTransactions.map(t => t.id);
    const value: EditManyTransactionsActionValue = {
      bucketId: bucket.id,
      ids: transactionIds,
    };

    this.transactionDataSource().clearSelected();

    const action: EditManyTransactionsAction = {
      type: 'EDIT_MANY_TRANSACTIONS',
      value,
    };

    return this.transactionDataSource().dispatch(action);
  }

  private scheduleViewportSync() {
    requestAnimationFrame(() => this.viewPort()?.checkViewportSize());
  }
}
